import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError
} from '@remix-run/react';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import { useEffect } from 'react';

import AwsChallengeScript from '~/components/AwsChallengeScript';
import Error404 from '~/components/Error404';
import GoogleTagManger from '~/components/GoogleTagManager';
import Modals from '~/components/Modals';
import SegmentScript from '~/components/SegmentScript';
import PageViewEvent from '~/events/PageViewEvent';
import useAwsGetToken from '~/hooks/useAwsGetToken';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import algolia from '~/services/algolia.client';
import braze from '~/services/braze.client';
import wct from '~/services/wct.client';
import styles from '~/styles/app.css?url';

import CookiePrompt from './components/CookiePrompt';
import RegionSwitch from './components/RegionSwitch';
import useRegion from './hooks/useRegion';
import { regions } from './utils/region';

export type OutletContext = {
  brazeInitialised: boolean;
};

export function links() {
  return [
    { href: styles, rel: 'stylesheet' },
    {
      as: 'script',
      href: 'https://goto-offer.com/wct.js?type=session',
      rel: 'preload'
    }
  ];
}

export const handle = { i18n: ['common'] };

export { loader } from './rootLoader';

function App() {
  const { env, clientIp, isBot, region } = useRootData();
  const user = useUser();

  const accessToken = user.accessToken;
  const refreshToken = user.refreshToken;

  const marketingCookies = useMarketingCookies();

  useAwsGetToken();

  useEffect(() => {
    marketingCookies.load();
  }, [marketingCookies]);

  useEffect(() => {
    algolia.init({
      apiKey: env.ALGOLIA_API_KEY,
      appId: env.ALGOLIA_APP_ID,
      productIndex: env.ALGOLIA_PRODUCTS_INDEX
    });
  }, [env.ALGOLIA_API_KEY, env.ALGOLIA_APP_ID, env.ALGOLIA_PRODUCTS_INDEX]);

  useEffect(() => {
    braze.init();

    if (user.isLoggedIn) braze.setUser(user.id);
  }, [env.BRAZE_API_KEY, env.BRAZE_BASE_URL, user.isLoggedIn, user.id]);

  useEffect(() => {
    wct.init(env.GA_TRACKING_ID);
  }, [env.GA_TRACKING_ID]);

  useEffect(() => {
    new PageViewEvent({ clientIp, isBot, user }).send();
  }, [clientIp, user, isBot]);

  useEffect(() => {
    if (accessToken) localStorage.setItem('JWT_ACCESS_TOKEN', accessToken);
    if (refreshToken) localStorage.setItem('JWT_REFRESH_TOKEN', refreshToken);
  }, [accessToken, refreshToken]);

  return (
    <html lang={region.lang}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {/* Apple login script & config */}
        <script src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_GB/appleid.auth.js" />
        <meta name="appleid-signin-client-id" content={env.APPLE_CLIENT_ID} />
        <meta name="appleid-signin-scope" content="name email" />
        <meta name="appleid-signin-redirect-uri" content={region.baseUrl} />
        <meta name="appleid-signin-use-popup" content="true" />
        {/* Ahrefs script */}
        <script
          src="https://analytics.ahrefs.com/analytics.js"
          data-key="LpPa4ofBwVRQTmiwMw71RQ"
          async
        />
        <Meta />
        <Links />
        <SegmentScript />
        <AwsChallengeScript />
        {process.env.NODE_ENV !== 'development' && <CookiePrompt />}
      </head>
      <body>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(env)}`
          }}
        />
        <GoogleTagManger />
        <Modals.EmailConfirmation />
        <Outlet />
        <ScrollRestoration />
        <Scripts />
        <GermanRegionSwitch />
      </body>
    </html>
  );
}

function GermanRegionSwitch() {
  const location = useLocation();
  const { isGermanIp } = useRootData();
  const region = useRegion();

  if (!isGermanIp || region.code === 'DE') return null;

  const germanRegion = regions.find((r) => r.code === 'DE');

  if (!germanRegion) return null;

  return <RegionSwitch targetRegion={germanRegion} path={location.pathname} />;
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();

  if (process.env.NODE_ENV === 'development') console.error(error);

  captureRemixErrorBoundaryError(error);

  return <Error404 />;
}
